import { z } from "zod";

/** Who is driving a thread's browser, as the takeover controls show it. */
export type BrowserTakeoverState = "unavailable" | "agent" | "stopping" | "user";

export const browserTakeoverSurfaceSchema = z.enum(["web", "ios"]);
export type BrowserTakeoverSurface = z.infer<typeof browserTakeoverSurfaceSchema>;

export type BrowserTakeoverStatusResp = {
  state: BrowserTakeoverState;
  taskId?: string;
  /** Identifies one takeover; every later call names it, so a stale screen cannot act. */
  controlId?: string;
  expiresAtMs?: number;
  /** Present while private sign-in can be completed manually, or its browser is held. */
  signInArtifactId?: string;
  credentialRequest?: { origin: string };
  /**
   * The thread's browser is gone, but its request can be picked back up. Carried alongside
   * `state: "unavailable"` rather than as a state of its own: the bars switch on `state`
   * without a default clause, so older clients ignore this hint and keep showing nothing.
   */
  restart?: { taskId: string; revision: number };
};

export const takeBrowserControlReq = z.object({
  taskId: z.string().min(1),
  surface: browserTakeoverSurfaceSchema,
});
export type TakeBrowserControlReq = z.infer<typeof takeBrowserControlReq>;

export const browserControlReq = z.object({ controlId: z.string().min(1) });
export const browserLiveViewReq = browserControlReq.extend({
  interactiveViewer: z.boolean().optional(),
  /** Omitted by older clients, which keep the browser's current viewport. */
  surface: browserTakeoverSurfaceSchema.optional(),
});
export const watchBrowserReq = z.object({
  taskId: z.string().min(1),
  interactiveViewer: z.boolean().optional(),
});
export const browserConnectionEventSchema = z.object({
  connectionId: z.string().min(1).max(100),
  phase: z.enum([
    "url_requested",
    "url_ready",
    "signaling",
    "media_ready",
    "input_ready",
    "watching",
    "user_control",
    "reconnecting",
    "unavailable",
    "autoplay_blocked",
  ]),
  attemptNumber: z.number().int().min(1).max(3),
  durationMs: z.number().int().min(0).max(120000),
  errorCode: z
    .enum(["startup_timeout", "transport_failed", "url_unavailable", "media_failed"])
    .optional(),
});
export type BrowserConnectionEvent = z.infer<typeof browserConnectionEventSchema>;
export const browserConnectionAttemptSchema = z.object({
  connectionId: z.string().min(1).max(100),
  attemptNumber: z.number().int().min(1).max(3),
  deadlineAtMs: z.number().int().positive(),
});
export type BrowserConnectionAttempt = z.infer<typeof browserConnectionAttemptSchema>;
export type BrowserConnectionRequest =
  | string
  | { id: string; connection: BrowserConnectionAttempt };
export const browserConnectionLogReq = browserConnectionEventSchema.extend({
  taskId: z.string().min(1),
});
export type BrowserControlReq = z.infer<typeof browserControlReq>;

/** A live view link is a bearer capability for the whole browser; it is never stored. */
export type BrowserLiveViewResp = {
  url: string;
  viewerUrl?: string;
  windowUrl?: string;
  mode?: "watch" | "control";
};
